import { getStoredLoadout, getWieldedLoadout, type CrucibleMember, type StartingCrystalPick } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import {
    CarriedPanel,
    CarriedSlotContext,
    EssenceVessel,
    ItemTransferProvider,
    Loadout,
    useItemTransferContext,
    type ItemMenuBuilder,
} from '@sorrem-client/ui/components';
import { useAbilityPickerMenu } from '@sorrem-client/ui/features/AbilityPicker';
import { listCarriedSlotHotkeyIndexes, withCarriedSlotHotkey } from '@sorrem-client/ui/shared/action-bar';
import { HudAreaSlot } from '@sorrem-client/ui/features/Hud';
import { canMoveItem } from '@sorrem-client/ui/shared/equipment-slots';
import { resolveItemMenuEntries, toItemMenuItems } from '@sorrem-client/ui/shared/item-menu';
import { useLoadoutActions } from '@sorrem-client/ui/features/loadout-actions';
import { useLoadoutEquipment } from '@sorrem-client/ui/features/loadout-equipment';
import { useActionBarTargets, useInputBindingPress } from '@sorrem-client/ui/hooks';
import { useCallback, useMemo, useState, type ComponentProps } from 'react';
import { useStartingCrystalStaging, type StartingCrystalStaging } from '../use-starting-crystal-staging';
import styles from './GatheringInventory.module.scss';

type ItemTransferProps = ComponentProps<typeof ItemTransferProvider>;
type MoveToHandler = NonNullable<ItemTransferProps['onMoveTo']>;
type CanMoveToHandler = NonNullable<ItemTransferProps['canMoveTo']>;

/** Crystals coalesced here are ready at once, so nothing is ever coalescing. */
const NO_COALESCENCES: readonly never[] = [];

interface GatheringInventoryProps {
    readonly members: readonly CrucibleMember[];
    /** The member is ready (or the run is starting): gear and crystals can't change. */
    readonly isLocked: boolean;
    readonly stageStartingCrystals: (picks: StartingCrystalPick[]) => Promise<string | null>;
}

/**
 * The entering character's Carried panel and loadout on the gathering page:
 * the same panel as in a run, doing the same things, except that crystals
 * coalesce at once from the character's banked essence (charged when the run
 * starts) and are ready the moment they enter. Gear moves are saved to the
 * character. The panel goes in the Carried HUD area, as in a run.
 */
export function GatheringInventory({ members, isLocked, stageStartingCrystals }: GatheringInventoryProps): JSX.Element {
    const staging = useStartingCrystalStaging({ members, stageStartingCrystals });
    const [status, setStatus] = useState<string | null>(null);
    const { moveItem, carriedItems, character, totalSlots } = staging;

    const handleMoveTo = useCallback<MoveToHandler>((selection, target) => {
        if (isLocked || (target.container !== 'inventory' && target.container !== 'equipment')) {
            return;
        }
        setStatus(null);
        void moveItem(selection.itemId, { container: target.container, index: target.index }).then(setStatus);
    }, [isLocked, moveItem]);

    // The same rules as in a run, so a move the server would refuse is refused here first.
    const canMoveTo = useCallback<CanMoveToHandler>((selection, target) => (
        !isLocked && character !== null && canMoveItem({ equipped: getStoredLoadout(character), carriedItems, carriedCapacity: totalSlots }, selection, target)
    ), [carriedItems, character, isLocked, totalSlots]);

    // An item's right-click menu (#267): Equip or Unequip and Details; only Details once the gathering is locked.
    const itemMenu = useCallback<ItemMenuBuilder>((selection, { selectItem }) => {
        if (character === null) {
            return [];
        }
        const entries = resolveItemMenuEntries(
            { equipped: getStoredLoadout(character), carriedItems, carriedCapacity: totalSlots },
            selection,
            { hasStorage: false, canTrade: false, canUse: false, canDrop: false },
        );
        return toItemMenuItems(isLocked ? entries.filter((entry) => entry.action === 'details') : entries, {
            move: (target) => handleMoveTo(selection, target),
            details: () => selectItem(selection.itemId, selection.container),
        });
    }, [carriedItems, character, handleMoveTo, isLocked, totalSlots]);

    return (
        <ItemTransferProvider onMoveTo={handleMoveTo} canMoveTo={canMoveTo} itemMenu={itemMenu}>
            <GatheringInventoryContent
                staging={staging}
                isLocked={isLocked}
                status={status}
                onStatusChange={setStatus}
            />
        </ItemTransferProvider>
    );
}

interface GatheringInventoryContentProps {
    readonly staging: StartingCrystalStaging;
    readonly isLocked: boolean;
    readonly status: string | null;
    readonly onStatusChange: (status: string | null) => void;
}

function GatheringInventoryContent({ staging, isLocked, status, onStatusChange }: GatheringInventoryContentProps): JSX.Element {
    const { character, carriedItems, totalSlots, availableEssence, coalesce, removeCrystal, equip } = staging;
    const { targets, setTargets } = useActionBarTargets();
    const { selectedItemId, clearSelectedItem } = useItemTransferContext();
    const [isOpen, setIsOpen] = useState(true);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
    const [linkingSlotIndex, setLinkingSlotIndex] = useState<number | null>(null);
    const [isVesselOpen, setIsVesselOpen] = useState(false);

    const setOpen = useCallback((open: boolean): void => {
        setIsOpen(open);
        if (!open) {
            setSelectedSlotIndex(null);
            setLinkingSlotIndex(null);
        }
    }, []);
    useInputBindingPress('toggle-inventory', () => setOpen(!isOpen));
    const abilityPicker = useAbilityPickerMenu(character?.skills, character ? getStoredLoadout(character) : undefined);
    // Esc backs out one step at a time: the ability picker, a waiting hotkey label, then the selection.
    useInputBindingPress('open-menu', abilityPicker.isOpen || linkingSlotIndex !== null || selectedItemId !== null || selectedSlotIndex !== null
        ? (): void => {
            if (abilityPicker.isOpen) {
                // The picker closes itself on Esc too; this only keeps the same press from backing out further.
                abilityPicker.close();
            } else if (linkingSlotIndex !== null) {
                setLinkingSlotIndex(null);
            } else {
                clearSelectedItem();
                setSelectedSlotIndex(null);
            }
        }
        : undefined);

    // Frames, details and item cards show the gear as wielded: an item the character falls short of comes back weakened.
    const wieldedLoadout = useMemo(() => (character ? getWieldedLoadout(character) : undefined), [character]);
    const equipment = useLoadoutEquipment(wieldedLoadout, carriedItems);
    const caster = useMemo(
        () => (character ? { essence: character.essence, skills: character.skills, wieldedLoadout } : undefined),
        [character, wieldedLoadout],
    );
    const { actions, actionDetails } = useLoadoutActions(targets, carriedItems, caster);
    const activeLinkingSlotIndex = isOpen ? linkingSlotIndex : null;
    const selectedActions = useMemo(() => (selectedSlotIndex === null || !isOpen
        ? []
        : listCarriedSlotHotkeyIndexes(targets, selectedSlotIndex)
    ), [isOpen, selectedSlotIndex, targets]);

    // Before the run there's nothing to trigger; an action key only links a waiting carried slot.
    const handleActionActivate = useCallback((index: number): void => {
        if (activeLinkingSlotIndex === null) {
            return;
        }
        setTargets(withCarriedSlotHotkey(targets, index, activeLinkingSlotIndex));
        setLinkingSlotIndex(null);
    }, [activeLinkingSlotIndex, setTargets, targets]);

    const handleEquip = useCallback((item: UIInventoryItem): void => {
        onStatusChange(null);
        clearSelectedItem();
        setSelectedSlotIndex(null);
        void equip(item).then(onStatusChange);
    }, [clearSelectedItem, equip, onStatusChange]);

    // No slot actions (coalesce, remove, equip) while locked; hotkey linking stays, as it changes nothing the run charges.
    const contextSlotIndex = !isLocked && selectedSlotIndex !== null && selectedSlotIndex < totalSlots ? selectedSlotIndex : null;

    return (
        <>
            <HudAreaSlot panel="carried">
                <CarriedPanel
                    open={isOpen}
                    onOpenChange={setOpen}
                    carriedItems={carriedItems}
                    totalSlots={totalSlots}
                    selectedSlotIndex={selectedSlotIndex}
                    onSelectedSlotChange={setSelectedSlotIndex}
                    assigningSlotIndex={linkingSlotIndex}
                    onAssigningSlotChange={setLinkingSlotIndex}
                    status={status}
                    context={contextSlotIndex === null || wieldedLoadout === undefined ? null : (
                        <CarriedSlotContext
                            slotIndex={contextSlotIndex}
                            item={carriedItems[contextSlotIndex]}
                            pending={NO_COALESCENCES}
                            essencePool={availableEssence}
                            isInstant
                            onStartCoalesce={(recipe) => coalesce(contextSlotIndex, recipe)}
                            onCancelCoalesce={() => undefined}
                            crystalAction={{
                                label: 'Remove',
                                onClick: () => {
                                    onStatusChange(null);
                                    void removeCrystal(contextSlotIndex).then(onStatusChange);
                                },
                            }}
                            equipped={wieldedLoadout}
                            onEquip={handleEquip}
                        />
                    )}
                />
            </HudAreaSlot>
            <div className={styles.loadoutDock}>
                <Loadout
                    essence={character?.essence ?? 0}
                    maxEssence={character?.maxEssence ?? 0}
                    onActionActivate={handleActionActivate}
                    actions={actions}
                    actionDetails={actionDetails}
                    {...abilityPicker.loadoutProps}
                    hasHighlightedActions={activeLinkingSlotIndex !== null}
                    selectedActions={selectedActions}
                    {...equipment}
                    vessel={<EssenceVessel essence={availableEssence} label='Essence in your vessel' />}
                    isVesselOpen={isVesselOpen}
                    onVesselOpenChange={setIsVesselOpen}
                />
            </div>
        </>
    );
}
